import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import Stack from '@mui/joy/Stack';
import Button from '@mui/joy/Button';
import Drawer from '@mui/joy/Drawer';
import Sheet from '@mui/joy/Sheet';
import Input from '@mui/joy/Input';
import ModalClose from '@mui/joy/ModalClose';
import Typography from '@mui/joy/Typography';
import Tooltip from '@mui/joy/Tooltip';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Textarea from '@mui/joy/Textarea';
import Chip from '@mui/joy/Chip';


import { Error } from 'components/FancyToast';
import FileUpload from './FileUpload';

import { getIcon } from 'utils/misc';
import useEmail from 'hooks/useEmail';
import useWorkKey from 'hooks/useWorkKey';
import useUserIdentity from 'hooks/useUserIdentity';
import { FileType } from 'stores/file_store';
// import { EmailProps } from 'stores/email_store';

import localforage from 'localforage';


export type EmailSubmitProps = {
    files: FileType[];
    content: string;
    content_type: string;
    subject: string;
    to_email: string;
};

const PREFIX = 'EMAIL';
function _cache_key(uuid: string) {
    return `${PREFIX}/${uuid}`;
}

export function deleteCachedComment(uuid: string) {
     console.log("deleteing cached comment: %s", uuid);
    return localforage.removeItem(_cache_key(uuid));
}

function cacheComment(uuid: string, comment: EmailSubmitProps) {
    console.log("caching comment: %s, %o", uuid, comment);
    return localforage.setItem(_cache_key(uuid), comment);
}

// async function getCachedComment(uuid: string) {
//     console.log("getCachedComment: %s", uuid);
//     return localforage.getItem<EmailSubmitProps>(_cache_key(uuid));
// }


export type EmailSenderProps = {
    associated_type: string;
    associated_uuid: string;
    from_email: string;
    onSent: () => void;
};

const EmailSender = observer(({
    associated_type,
    associated_uuid,
    from_email,
    onSent }: EmailSenderProps) => {

    const workKey = useWorkKey();
    const { sendEmail } = useEmail();
    const { currentIdentity } = useUserIdentity();
    const [showEmailDrawer, setEmailDrawer] = useState(false);
    const [to_email, setToEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
        const [sending, setSending] = useState(false);


        // file upload
        const [isUploading, setIsUploading] = useState(false);
        const [files, setFiles] = useState<FileType[]>([] as FileType[]);

    const EmailIcon = getIcon('MailOutline');
    const SendIcon = getIcon('Send');

    const uuid = `${associated_type}-${associated_uuid}-comment`;


    useEffect(() => {
        doCache();
    }, [files.length]);

    const doCache = () => {
        cacheComment(uuid, {
            files,
            subject,
            content,
            content_type: 'text/plain',
            to_email,
        });
    };

    function handleFilesChange(f: FileType[]) {
        if (JSON.stringify(f) != JSON.stringify(files)) {
            setFiles(f);
            console.log("Set files and doingcache: ", f);
            doCache();
            // cacheComment(uuid, {
            //     files: f,
            //     content: textAreaValue,
            //     content_type: 'text/plain',
            // });
        }
        else {
        }
    }


    function doSend() {
        if (!workKey) {
            Error("Can't send email, no identity loaded");
            return;
        }
        setSending(true);
        sendEmail(
            workKey,
            associated_type,
            associated_uuid,
            to_email,
            from_email,
            subject,
            content,
            files
        ).then(() => {
            setToEmail('');
            setSubject('');
            setContent('');
            onSent();
            setEmailDrawer(false);
            setFiles([]);
            deleteCachedComment(uuid);
        }).catch((err) => {
            Error(err);
        }).finally(() => {
            setSending(false);
        });

        console.log('Sending email to:', to_email,
            'with subject:', subject,
            'and content:', content);


        onSent && onSent();
    }

        if(currentIdentity.role !== 'assistant')
            return null;

    return (
        <>
            <Stack
                px={1}
                spacing={1} >
                <Tooltip
                    placement="top"
                    arrow title="Open the emailer">
                    <Button
                        size="sm"
                        variant="plain"
                        startDecorator={<EmailIcon />}
                        onClick={() => setEmailDrawer(!showEmailDrawer)} >
                        Email
                    </Button>
                </Tooltip>
            </Stack>
            <Drawer
                anchor="left"
                size="lg"
                variant="plain"
                open={showEmailDrawer}
                onClose={() => setEmailDrawer(false)}
                slotProps={{
                    content: {
                        sx: {
                            bgcolor: 'transparent',
                            p: { md: 3, sm: 0 },
                            boxShadow: 'none',
                        },
                    },
                }}
            >

                <Sheet
                    sx={{
                        borderRadius: 'md',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        height: '100%',
                        overflow: 'auto',
                    }}
                >

                    <ModalClose />

                    <Stack
                        pt={1}
                        spacing={3}
                    >
                        <Stack>
                            <Typography level="title-lg">
                                Send an Email
                            </Typography>
                            <Typography level="body-sm">
                                Send an email to a third party (not the client)
                            </Typography>
                        </Stack>

                        <FormControl>
                            <FormLabel htmlFor="to_email">From</FormLabel>
                            <Stack
                                direction="row">
                                <Tooltip
                                    arrow
                                    title="We'll save the replies on this page.">
                                    <Chip>{ currentIdentity.name } &lt;{ from_email }&gt;</Chip>
                                </Tooltip>
                            </Stack>
                        </FormControl>

                        <FormControl>
                            <FormLabel htmlFor="to_email">To</FormLabel>
                            <Input
                                onChange={(e) => setToEmail(e.target.value)}
                                value={to_email}
                                id="to_email"
                                type="email"
                                placeholder="Email"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel htmlFor="subject">Subject</FormLabel>
                            <Input
                                onChange={(e) => setSubject(e.target.value)}
                                value={subject}
                                id="subject"
                                type="text"
                                placeholder="Subject"
                            />
                        </FormControl>


                        <FormControl>
                        <FormLabel htmlFor="content">Content</FormLabel>

                            <Textarea
                                onChange={(e) => setContent(e.target.value)}
                                minRows={4}
                                value={content}
                                id="content"
                                placeholder="Content..."
            />

                            <FileUpload
                                defaultFilesUploaded={files}
                  uploadUuid={0}

                                setIsUploading={setIsUploading}
                                onFilesUploaded={handleFilesChange}
                            />

                        </FormControl>

                        <FormControl>
                            <FormLabel htmlFor="send-it">Send it</FormLabel>
                            {!sending &&
                                <Button
                                    id="send-it"
                                    color="primary"
                                    disabled={sending || isUploading}
                                    endDecorator={<SendIcon />}
                                    onClick={doSend}>
                                    Send
                                </Button>
                            }
                            {sending &&
                                <Typography level="body-xs">
                                    Sending
                                </Typography>}

                        </FormControl>

                    </Stack>
                </Sheet>
            </Drawer>
        </>
    );

});

export default EmailSender;
